import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  User, 
  Building, 
  Phone, 
  AlertCircle, 
  Loader,
  CheckCircle,
  Plus
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    agency: '',
    position: '',
    department: '',
    phone: '',
    role: 'IT Officer',
    customAgency: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)
  const [agencies, setAgencies] = useState([])
  const [loadingAgencies, setLoadingAgencies] = useState(true)
  const [showCustomAgency, setShowCustomAgency] = useState(false)
  const [agencySearchTerm, setAgencySearchTerm] = useState('')
  const [filteredAgencies, setFilteredAgencies] = useState([])
  const { signUp, loading, error } = useAuth()
  const navigate = useNavigate()

  // Fetch active agencies on component mount
  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        setLoadingAgencies(true)
        const { data, error } = await supabase
          .from('agencies')
          .select('id, name')
          .eq('is_active', true)
          .order('name')

        if (error || !data) {
          console.log('Using default agencies (database table may not exist)')
          // Fallback to default agencies if table doesn't exist
          const defaultAgencies = [
            { id: 'dict', name: 'Department of Information and Communications Technology (DICT)' },
            { id: 'npc', name: 'National Privacy Commission (NPC)' },
            { id: 'cicc', name: 'Cybercrime Investigation and Coordinating Center (CICC)' },
            { id: 'dnd', name: 'Department of National Defense (DND)' },
            { id: 'afp', name: 'Armed Forces of the Philippines (AFP)' },
            { id: 'pnp', name: 'Philippine National Police (PNP)' },
            { id: 'nbi', name: 'National Bureau of Investigation (NBI)' },
            { id: 'doj', name: 'Department of Justice (DOJ)' },
            { id: 'bfp', name: 'Bureau of Fire Protection (BFP)' },
            { id: 'op', name: 'Office of the President' }
          ]
          setAgencies([...defaultAgencies, { id: 'others', name: 'Others (Please specify)' }])
        } else {
          setAgencies([...(data || []), { id: 'others', name: 'Others (Please specify)' }])
        }
      } catch (error) {
        console.error('Error in fetchAgencies:', error)
        // Final fallback to default agencies
        const defaultAgencies = [
          { id: 'dict', name: 'Department of Information and Communications Technology (DICT)' },
          { id: 'npc', name: 'National Privacy Commission (NPC)' },
          { id: 'cicc', name: 'Cybercrime Investigation and Coordinating Center (CICC)' },
          { id: 'dnd', name: 'Department of National Defense (DND)' },
          { id: 'afp', name: 'Armed Forces of the Philippines (AFP)' },
          { id: 'pnp', name: 'Philippine National Police (PNP)' },
          { id: 'nbi', name: 'National Bureau of Investigation (NBI)' },
          { id: 'doj', name: 'Department of Justice (DOJ)' },
          { id: 'bfp', name: 'Bureau of Fire Protection (BFP)' },
          { id: 'op', name: 'Office of the President' }
        ]
        setAgencies([...defaultAgencies, { id: 'others', name: 'Others (Please specify)' }])
      } finally {
        setLoadingAgencies(false)
      }
    }

    fetchAgencies()
  }, [])

  // Update filtered agencies when agencies list changes
  useEffect(() => {
    setFilteredAgencies(agencies)
  }, [agencies])

  // Handle agency selection change
  const handleAgencyChange = (e) => {
    const value = e.target.value
    setFormData(prev => ({
      ...prev,
      agency: value,
      customAgency: value === 'Others' ? prev.customAgency : ''
    }))
    
    setShowCustomAgency(value === 'Others')
    
    // Clear error when user starts typing
    if (errors.agency) {
      setErrors(prev => ({
        ...prev,
        agency: ''
      }))
    }
  }

  // Handle agency search/filter
  const handleAgencySearch = (searchTerm) => {
    setAgencySearchTerm(searchTerm);
    
    if (!searchTerm.trim()) {
      setFilteredAgencies(agencies);
      return;
    }
    
    const normalizedSearch = searchTerm.toLowerCase().trim();
    const filtered = agencies.filter(agency => 
      agency.name.toLowerCase().includes(normalizedSearch) ||
      agency.id.toLowerCase().includes(normalizedSearch) ||
      // Also check for common abbreviations
      (agency.name.includes('DICT') && normalizedSearch.includes('dict')) ||
      (agency.name.includes('CICC') && normalizedSearch.includes('cicc')) ||
      (agency.name.includes('Cybercrime') && normalizedSearch.includes('cybercrime'))
    );
    
    setFilteredAgencies(filtered);
  }

  // Helper function to check for similar agency names
  const findSimilarAgency = (inputName) => {
    if (!inputName || inputName.trim() === '') return null;
    
    const normalizedInput = inputName.toLowerCase().trim();
    const similarThreshold = 0.6; // 60% similarity threshold (more lenient)
    
    for (const agency of agencies) {
      const normalizedAgencyName = agency.name.toLowerCase();
      
      // Check for exact match first
      if (normalizedAgencyName === normalizedInput) {
        return agency;
      }
      
      // Check for partial matches
      if (normalizedAgencyName.includes(normalizedInput) || normalizedInput.includes(normalizedAgencyName)) {
        return agency;
      }
      
      // Check for key terms match with word variations
      const agencyTerms = normalizedAgencyName.replace(/[()]/g, '').split(/\s+/);
      const inputTerms = normalizedInput.split(/\s+/);
      
      let matchCount = 0;
      for (const inputTerm of inputTerms) {
        if (agencyTerms.some(term => {
          // Exact match
          if (term === inputTerm) return true;
          // Partial match
          if (term.includes(inputTerm) || inputTerm.includes(term)) return true;
          // Word stem match (for variations like coordinating/coordination)
          if (term.length > 4 && inputTerm.length > 4) {
            const termStem = term.substring(0, 4);
            const inputStem = inputTerm.substring(0, 4);
            if (termStem === inputStem) return true;
          }
          return false;
        })) {
          matchCount++;
        }
      }
      
      const similarity = matchCount / Math.max(agencyTerms.length, inputTerms.length);
      if (similarity >= similarThreshold) {
        return agency;
      }
      
      // Special check for common agency abbreviations and key terms
      if (normalizedAgencyName.includes('cybercrime') && normalizedInput.includes('cybercrime')) {
        return agency;
      }
      if (normalizedAgencyName.includes('dict') && normalizedInput.includes('dict')) {
        return agency;
      }
      if (normalizedAgencyName.includes('cicc') && normalizedInput.includes('cicc')) {
        return agency;
      }
    }
    
    return null;
  }

  const roles = [
    { value: 'IT Officer', label: 'IT Officer' },
    { value: 'DPO', label: 'DPO (Data Protection Officer)' }
  ]

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required'
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    
    if (!formData.agency.trim()) {
      newErrors.agency = 'Agency/Office is required'
    }
    
    if (!formData.position.trim()) {
      newErrors.position = 'Position is required'
    }
    
    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    // Handle custom agency if "Others" was selected
    let finalAgency = formData.agency
    
    if (formData.agency === 'Others' && formData.customAgency.trim()) {
      finalAgency = formData.customAgency.trim()
      
      // Check if the custom agency name is similar to an existing one
      const similarAgency = findSimilarAgency(finalAgency);
      if (similarAgency) {
        console.log(`🤔 Custom agency "${finalAgency}" is similar to existing agency: "${similarAgency.name}"`);
        
        // Show confirmation to user (in a real app, you'd show a modal)
        const useExisting = window.confirm(
          `Did you mean "${similarAgency.name}" instead of "${finalAgency}"?\n\n` +
          `Click OK to use the existing agency, or Cancel to continue with your custom entry.`
        );
        
        if (useExisting) {
          finalAgency = similarAgency.name;
          console.log(`✅ User chose existing agency: ${finalAgency}`);
        } else {
          console.log(`📝 User chose to keep custom agency: ${finalAgency}`);
        }
      }
      
      // Create pending agency request
      try {
        await supabase.from('pending_agencies').insert([
          {
            name: finalAgency,
            suggested_by_user_id: null // Will be updated after user creation
          }
        ])
      } catch (error) {
        console.error('Error creating pending agency:', error)
        // Continue with registration even if pending agency creation fails
      }
    }
    
    // Debug logging to track agency values
    console.log('📋 REGISTRATION FORM DATA:');
    console.log('- Original agency selection:', formData.agency);
    console.log('- Custom agency (if Others):', formData.customAgency);
    console.log('- Final agency to be saved:', finalAgency);
    
    const userData = {
      name: formData.name,
      agency: finalAgency,
      position: formData.position,
      department: formData.department,
      phone: formData.phone,
      role: formData.role,
      user_type: 'government_employee',
      approval_status: 'pending'
    }
    
    console.log('🚀 SUBMITTING USER DATA:', JSON.stringify(userData, null, 2));
    
    const { data, error } = await signUp(formData.email, formData.password, userData)
    
    if (error) {
      console.error('❌ Signup error:', error);
    } else {
      console.log('✅ Signup successful, user ID:', data.user?.id);
    }
    
    if (!error && data.user) {
      // Create registration approval request
      try {
        await supabase.from('user_registration_approvals').insert([
          {
            user_id: data.user.id,
            status: 'pending'
          }
        ])
        
        // Update pending agency with user ID if custom agency was used
        if (formData.agency === 'Others' && formData.customAgency.trim()) {
          await supabase
            .from('pending_agencies')
            .update({ suggested_by_user_id: data.user.id })
            .eq('name', finalAgency)
        }
        
        // Send notification to admins (this would be implemented with a trigger or function)
        console.log('Registration submitted for admin approval')
        
      } catch (approvalError) {
        console.error('Error creating approval request (table may not exist):', approvalError)
        // Continue with registration even if approval table doesn't exist
      }
      
      setSuccess(true)
      // Do NOT automatically redirect to dashboard for government employees
      // They need to wait for admin approval before accessing the system
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gov-800 mb-2">
              Registration Submitted Successfully!
            </h2>
            <p className="text-gov-600 mb-4">
              Your account has been created and is pending admin approval. You will be redirected to the login page shortly.
            </p>
            <p className="text-sm text-gov-500 mb-4">
              Please wait for an administrator to approve your registration before you can log in. You will receive an email notification once approved.
            </p>
            <div className="flex justify-center">
              <Loader className="h-6 w-6 animate-spin text-cyber-600" />
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl w-full space-y-8"
      >
        <div>
          <div className="mx-auto h-12 w-12 bg-cyber-600 rounded-lg flex items-center justify-center">
            <User className="h-6 w-6 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-display font-bold text-gov-800">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gov-600">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-cyber-600 hover:text-cyber-500 transition-colors"
            >
              Sign in here
            </Link>
          </p>
        </div>
        
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mt-8 bg-white rounded-xl shadow-lg p-8"
          onSubmit={handleSubmit}
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3 mb-6"
            >
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </motion.div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gov-800 border-b border-gov-200 pb-2">
                Personal Information
              </h3>
              
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gov-700 mb-1">
                  Full Name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gov-400" />
                  </div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors ${
                      errors.name ? 'border-red-300 bg-red-50' : 'border-gov-300 bg-white'
                    }`}
                    placeholder="Enter your full name"
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gov-700 mb-1">
                  Email Address *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gov-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors ${
                      errors.email ? 'border-red-300 bg-red-50' : 'border-gov-300 bg-white'
                    }`}
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gov-700 mb-1">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gov-400" />
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors ${
                      errors.phone ? 'border-red-300 bg-red-50' : 'border-gov-300 bg-white'
                    }`}
                    placeholder="Enter your phone number"
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>
            </div>
            
            {/* Work Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gov-800 border-b border-gov-200 pb-2">
                Work Information
              </h3>
              
              <div>
                <label htmlFor="agency" className="block text-sm font-medium text-gov-700 mb-1">
                  Agency/Office *
                </label>
                
                {/* Agency Search Input */}
                <div className="relative mb-2">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building className="h-5 w-5 text-gov-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Type to search agencies..."
                    value={agencySearchTerm}
                    onChange={(e) => handleAgencySearch(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gov-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors bg-white"
                  />
                </div>
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building className="h-5 w-5 text-gov-400" />
                  </div>
                  <select
                    id="agency"
                    name="agency"
                    value={formData.agency}
                    onChange={handleAgencyChange}
                    disabled={loadingAgencies}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors appearance-none ${
                      errors.agency ? 'border-red-300 bg-red-50' : 'border-gov-300 bg-white'
                    } ${loadingAgencies ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="">{loadingAgencies ? 'Loading agencies...' : 'Select your agency/office'}</option>
                    {filteredAgencies.map(agency => (
                      <option key={agency.id} value={agency.name}>
                        {agency.name}
                      </option>
                    ))}
                    <option value="Others">Others (Please specify)</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gov-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {errors.agency && (
                  <p className="mt-1 text-sm text-red-600">{errors.agency}</p>
                )}
                {agencySearchTerm && filteredAgencies.length === 0 && (
                  <p className="mt-1 text-sm text-yellow-600">
                    No agencies found. Try a different search term or select "Others" to specify manually.
                  </p>
                )}
              </div>

              {/* Custom Agency Input - Only show when "Others" is selected */}
              {showCustomAgency && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2"
                >
                  <label htmlFor="customAgency" className="block text-sm font-medium text-gov-700 mb-1">
                    Please specify your agency *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Plus className="h-5 w-5 text-gov-400" />
                    </div>
                    <input
                      id="customAgency"
                      name="customAgency"
                      type="text"
                      value={formData.customAgency}
                      onChange={handleChange}
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors ${
                        errors.agency ? 'border-red-300 bg-red-50' : 'border-gov-300 bg-white'
                      }`}
                      placeholder="Enter your agency name"
                    />
                  </div>
                </motion.div>
              )}
              
              <div>
                <label htmlFor="position" className="block text-sm font-medium text-gov-700 mb-1">
                  Position *
                </label>
                <input
                  id="position"
                  name="position"
                  type="text"
                  value={formData.position}
                  onChange={handleChange}
                  className={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors ${
                    errors.position ? 'border-red-300 bg-red-50' : 'border-gov-300 bg-white'
                  }`}
                  placeholder="e.g., IT Specialist, Security Officer"
                />
                {errors.position && (
                  <p className="mt-1 text-sm text-red-600">{errors.position}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="department" className="block text-sm font-medium text-gov-700 mb-1">
                  Department/Division
                </label>
                <input
                  id="department"
                  name="department"
                  type="text"
                  value={formData.department}
                  onChange={handleChange}
                  className="block w-full px-3 py-3 border border-gov-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors bg-white"
                  placeholder="e.g., Cybersecurity Division"
                />
              </div>
              
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gov-700 mb-1">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="block w-full px-3 py-3 border border-gov-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors bg-white"
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Password Section */}
          <div className="mt-6 pt-6 border-t border-gov-200">
            <h3 className="text-lg font-semibold text-gov-800 mb-4">
              Security Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gov-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gov-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors ${
                      errors.password ? 'border-red-300 bg-red-50' : 'border-gov-300 bg-white'
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gov-400 hover:text-gov-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gov-400 hover:text-gov-600" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gov-700 mb-1">
                  Confirm Password *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gov-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyber-500 focus:border-transparent transition-colors ${
                      errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gov-300 bg-white'
                    }`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gov-400 hover:text-gov-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gov-400 hover:text-gov-600" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-8">
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-cyber-600 hover:bg-cyber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <Loader className="h-5 w-5 animate-spin" />
              ) : (
                'Create Account'
              )}
            </button>
          </div>
        </motion.form>
      </motion.div>
    </div>
  )
}

export default Register